import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { ApiService } from '../../abstract/api.service';
import { DjiFlightDetail } from '../data-models/dji-flight-detail.model';
import { DjiFlightRow } from '../data-models/dji-flight-row.model';
import { DjiLinkStatus } from '../data-models/dji-link-status.model';
import { DjiStatusCounts } from '../data-models/dji-status-counts.model';
import { DjiUnlinkTarget } from '../data-models/dji-unlink-target.model';
import { DjiFlightFilters } from '../request-models/dji-flight-filters.model';
import { DjiSetDroneRequest } from '../request-models/dji-set-drone-request.model';
import { ApiListResponse } from '../response-models/api-list-response.model';
import { DjiDroneRef } from '../response-models/dji-drone-ref.model';
import { DjiFlightList } from '../response-models/dji-flight-list.model';
import { DjiRelinkResult } from '../response-models/dji-relink-result.model';
import { OrganizationOption } from '../response-models/organization-option.model';
import { OrganizationService } from '../data-services/organization.service';
import { toListQuery } from '../utils/dji-flight.utils';
import { isRecord } from '../utils/is-record';
import { singlePage } from '../utils/paging-utils';

/**
 * DjiFlightAdminService (ADN-2451): the hub's `/admin/dji` endpoints (hub `admin-dji-controller.ts`), DJI
 * flights across every organization and the actions that resolve them. A flight is keyed by its intake id and
 * is a flattened view, so this is an api-service. The list is paged with `limit` (verified against the hub).
 *
 * Every failure is an `ApiError` carrying the hub's own message, or the method's fallback when the hub sent none.
 */
@Injectable({ providedIn: 'root' })
export class DjiFlightAdminService extends ApiService {
  private readonly organizationService = inject(OrganizationService);

  constructor() {
    super('admin/dji');
  }

  listFlights(filters: DjiFlightFilters = {}): Observable<DjiFlightList> {
    return this.requestEnvelope('GET', { path: 'flights', query: toListQuery(filters) }, toFlightList).pipe(
      withFallback('Failed to load DJI flights.'),
    );
  }

  getFlight(intakeId: string): Observable<DjiFlightDetail> {
    return this.request('GET', { path: ['flights', intakeId] }, data => data as DjiFlightDetail).pipe(
      withFallback('Failed to load this flight.'),
    );
  }

  /** Any drone by serial number or name (two characters or more). */
  searchDrones(search: string): Observable<DjiDroneRef[]> {
    return this.request('GET', { path: 'drones', query: { search } }, data => (Array.isArray(data) ? (data as DjiDroneRef[]) : [])).pipe(
      withFallback('Failed to search drones.'),
    );
  }

  setDroneAndOrganization(intakeId: string, body: DjiSetDroneRequest): Observable<DjiLinkStatus> {
    return this.linkAction(intakeId, 'set-drone', body, 'Failed to set the drone.');
  }

  unlink(intakeId: string, target: DjiUnlinkTarget): Observable<DjiLinkStatus> {
    return this.linkAction(intakeId, 'unlink', { target }, 'Failed to unlink.');
  }

  dismiss(intakeId: string, note?: string): Observable<DjiLinkStatus> {
    return this.linkAction(intakeId, 'dismiss', { note }, 'Failed to dismiss the flight.');
  }

  markInvalid(intakeId: string, note?: string): Observable<DjiLinkStatus> {
    return this.linkAction(intakeId, 'mark-invalid', { note }, 'Failed to mark the flight invalid.');
  }

  restore(intakeId: string): Observable<DjiLinkStatus> {
    return this.linkAction(intakeId, 'restore', {}, 'Failed to restore the flight.');
  }

  relink(intakeId: string): Observable<DjiRelinkResult> {
    return this.action(intakeId, 'relink', {}, data => data as DjiRelinkResult, 'Failed to re-run linkage.');
  }

  /** The organizations for the filter and the drone picker (the shared org picker). */
  listOrganizations(): Observable<OrganizationOption[]> {
    return this.organizationService.getOptions().pipe(
      map(page => page.items),
      withFallback('Failed to load organizations.'),
    );
  }

  private linkAction(intakeId: string, name: string, body: object, fallback: string): Observable<DjiLinkStatus> {
    return this.action(intakeId, name, body, data => (isRecord(data) ? data['linkStatus'] : undefined) as DjiLinkStatus, fallback);
  }

  private action<T>(intakeId: string, name: string, body: object, read: (data: unknown) => T, fallback: string): Observable<T> {
    return this.request('POST', { path: ['flights', intakeId, name] }, read, body).pipe(withFallback(fallback));
  }
}

function toFlightList(body: ApiListResponse<unknown> | null): DjiFlightList {
  const rows = Array.isArray(body?.data) ? (body.data as DjiFlightRow[]) : [];
  const counts = isRecord(body) && isRecord(body['statusCounts']) ? (body['statusCounts'] as DjiStatusCounts) : null;
  return {
    items: rows,
    paging: body?.paging ?? singlePage(rows.length),
    statusCounts: counts,
  };
}

/** Keeps the hub's own message; when it sent none, the same error with `fallback` as its message. */
function withFallback<T>(fallback: string) {
  return catchError<T, Observable<never>>(err => {
    const error = ApiError.fromHttpError(err);
    const result = error.errors.length > 0
      ? error
      : new ApiError(error.status, [], { code: error.code, action: error.action, detail: error.detail }, fallback);
    return throwError(() => result);
  });
}
